// LessonPlayer — orchestrates a lesson as a sequence of one-activity-at-a-time
// screens. docs/replan-v2.md R5 / CLAUDE.md §23.
//
// Design: the player owns flow + feedback chrome; it does NOT own persistence.
// The host app injects `onSubmitAttempt` + `onComplete` (which call the API via
// the app's own client). This keeps the package free of TanStack Query, the
// SDK, and the session cookie — it's pure render + flow.
//
// Flow per activity:
//   presentational (letter/vocabulary card): renderer fires onViewed on mount →
//     player records a "viewed" attempt in the background → "Continue" enabled.
//   scored (mc / listen / match / quiz): renderer fires onAnswer when committed →
//     player submits to the server → locks the activity, shows correct/incorrect
//     feedback → "Continue" advances. The server score is authoritative.
//
// On the last activity, "Continue" calls onComplete and shows the celebration.

import { useCallback, useMemo, useState, type ReactElement } from 'react';

import { content } from '@fajrarabi/zod-schemas';

import { DragSort } from './activities/DragSort';
import { FillBlank } from './activities/FillBlank';
import { IntroVideo } from './activities/IntroVideo';
import { LetterCard } from './activities/LetterCard';
import { LetterTrace } from './activities/LetterTrace';
import { ListenChoose } from './activities/ListenChoose';
import { MultipleChoice } from './activities/MultipleChoice';
import { PrintableHomework } from './activities/PrintableHomework';
import { QuizSummary } from './activities/QuizSummary';
import { ReadAloudPassage } from './activities/ReadAloudPassage';
import { RecordRepeat } from './activities/RecordRepeat';
import { StoryScene } from './activities/StoryScene';
import { TapMatch } from './activities/TapMatch';
import { VocabularyCard } from './activities/VocabularyCard';
import type { ActivityCallbacks, RenderContext, TFn } from './activities/types';

type ActivityDto = {
  id: string;
  type: content.ActivityType;
  sortOrder: number;
  payload: unknown;
  scoringRules: content.ScoringRules;
};

type AttemptResult = content.AttemptResultDto;
type CompleteResult = content.LessonCompleteResultDto;

export interface LessonPlayerProps {
  titleKey: string;
  activities: ActivityDto[];
  t: TFn;
  tashkeelMode: RenderContext['tashkeelMode'];
  cdnBase: string;
  isRtl: boolean;
  /** Persist one scored/viewed attempt; resolves with the server-scored result. */
  onSubmitAttempt: (activityId: string, attempt: content.AttemptInput) => Promise<AttemptResult>;
  /** Mark the lesson complete; resolves with final score + xp + streak. */
  onComplete: () => Promise<CompleteResult>;
  /** Leave the player (back to home / map). */
  onExit: () => void;
}

type Phase = 'playing' | 'submitting' | 'done';

export function LessonPlayer(props: LessonPlayerProps): ReactElement {
  const { activities, t, onSubmitAttempt, onComplete, onExit } = props;

  const ctx = useMemo<RenderContext>(
    () => ({
      t,
      tashkeelMode: props.tashkeelMode,
      cdnBase: props.cdnBase,
      isRtl: props.isRtl,
    }),
    [t, props.tashkeelMode, props.cdnBase, props.isRtl],
  );

  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('playing');
  // Per-activity state: whether the current activity is locked + its outcome.
  const [locked, setLocked] = useState(false);
  const [outcome, setOutcome] = useState<{ correct: boolean; passed: boolean } | null>(null);
  // "Continue" is enabled once a presentational card is viewed or a scored
  // activity is answered.
  const [canContinue, setCanContinue] = useState(false);
  const [finalResult, setFinalResult] = useState<CompleteResult | null>(null);
  const [error, setError] = useState(false);

  const total = activities.length;
  const current = activities[index];
  const isLast = index === total - 1;

  const resetForActivity = useCallback(() => {
    setLocked(false);
    setOutcome(null);
    setCanContinue(false);
    setError(false);
  }, []);

  const onViewed = useCallback(() => {
    // Presentational: record in the background, enable Continue immediately.
    if (!current) return;
    setCanContinue(true);
    void onSubmitAttempt(current.id, { kind: 'viewed' }).catch(() => {
      /* a failed "viewed" attempt is non-blocking — the kid can still continue */
    });
  }, [current, onSubmitAttempt]);

  const onAnswer = useCallback(
    (attempt: content.AttemptInput) => {
      if (!current || locked) return;
      setLocked(true);
      setPhase('submitting');
      void onSubmitAttempt(current.id, attempt)
        .then((res) => {
          setOutcome({ correct: res.correct, passed: res.passed });
          setCanContinue(true);
          setPhase('playing');
        })
        .catch(() => {
          // Let the kid retry on a network failure.
          setLocked(false);
          setError(true);
          setPhase('playing');
        });
    },
    [current, locked, onSubmitAttempt],
  );

  const cb: ActivityCallbacks = { onViewed, onAnswer, locked, outcome };

  const advance = useCallback(() => {
    if (isLast) {
      setPhase('submitting');
      void onComplete()
        .then((res) => {
          setFinalResult(res);
          setPhase('done');
        })
        .catch(() => {
          setError(true);
          setPhase('playing');
        });
      return;
    }
    setIndex((i) => i + 1);
    resetForActivity();
    setPhase('playing');
  }, [isLast, onComplete, resetForActivity]);

  if (phase === 'done' && finalResult) {
    return <Celebration result={finalResult} t={t} onExit={onExit} />;
  }

  if (!current) {
    // No activities — shouldn't happen for a published lesson, but render a safe
    // empty state rather than crashing.
    return (
      <div className="grid min-h-[60vh] place-items-center px-6 text-center">
        <p className="text-ink-700">{t('content.player.empty')}</p>
      </div>
    );
  }

  const progress = Math.round(((index + (canContinue ? 1 : 0)) / total) * 100);

  return (
    <div className="flex min-h-[70vh] flex-col" dir={ctx.isRtl ? 'rtl' : 'ltr'}>
      {/* Header: exit + progress bar */}
      <div className="flex items-center gap-3 px-1 pb-5">
        <button
          type="button"
          onClick={onExit}
          aria-label={t('content.player.exit')}
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-cream-100 text-xl text-burgundy-700 ring-1 ring-burgundy-500/10 transition-colors hover:bg-cream-200"
        >
          ✕
        </button>
        <div className="h-3 flex-1 overflow-hidden rounded-full bg-cream-200">
          <div
            className="h-full rounded-full bg-sun-500 transition-[width] duration-300"
            style={{ inlineSize: `${progress}%` }}
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
        <span className="shrink-0 text-sm font-bold text-ink-500">
          {index + 1}/{total}
        </span>
      </div>

      {/* Activity stage */}
      <div className="flex flex-1 items-center justify-center py-4">
        <ActivityStage activity={current} ctx={ctx} cb={cb} />
      </div>

      {/* Feedback + Continue */}
      <div className="px-1 pt-4">
        {outcome ? (
          <p
            className={`mb-3 text-center text-lg font-bold ${
              outcome.correct ? 'text-leaf' : 'text-heart'
            }`}
            role="status"
          >
            {outcome.correct ? t('content.player.correct') : t('content.player.try_again_feedback')}
          </p>
        ) : null}
        {error ? (
          <p className="mb-3 text-center text-sm text-heart" role="alert">
            {t('content.player.error')}
          </p>
        ) : null}
        <button
          type="button"
          onClick={advance}
          disabled={!canContinue || phase === 'submitting'}
          className="inline-flex min-h-[56px] w-full items-center justify-center rounded-full bg-burgundy-500 px-10 text-lg font-bold text-cream-50 transition-colors hover:bg-burgundy-600 disabled:opacity-40"
        >
          {phase === 'submitting'
            ? t('content.player.saving')
            : isLast
              ? t('content.player.finish')
              : t('content.player.continue')}
        </button>
      </div>
    </div>
  );
}

function ActivityStage({
  activity,
  ctx,
  cb,
}: {
  activity: ActivityDto;
  ctx: RenderContext;
  cb: ActivityCallbacks;
}): ReactElement {
  switch (activity.type) {
    case 'intro_video':
      return <IntroVideo data={content.introVideoData.parse(activity.payload)} ctx={ctx} cb={cb} />;
    case 'letter_card':
      return <LetterCard data={content.letterCardData.parse(activity.payload)} ctx={ctx} cb={cb} />;
    case 'vocabulary_card':
      return (
        <VocabularyCard
          data={content.vocabularyCardData.parse(activity.payload)}
          ctx={ctx}
          cb={cb}
        />
      );
    case 'multiple_choice':
      return <MultipleChoice data={activity.payload as never} ctx={ctx} cb={cb} />;
    case 'listen_choose':
      return <ListenChoose data={activity.payload as never} ctx={ctx} cb={cb} />;
    case 'tap_match':
      return <TapMatch data={activity.payload as never} ctx={ctx} cb={cb} />;
    case 'drag_sort':
      return <DragSort data={activity.payload as never} ctx={ctx} cb={cb} />;
    case 'fill_blank':
      return <FillBlank data={activity.payload as never} ctx={ctx} cb={cb} />;
    case 'quiz_summary':
      return <QuizSummary data={activity.payload as never} ctx={ctx} cb={cb} />;
    case 'record_repeat':
      return (
        <RecordRepeat data={content.recordRepeatData.parse(activity.payload)} ctx={ctx} cb={cb} />
      );
    case 'letter_trace':
      return (
        <LetterTrace data={content.letterTraceData.parse(activity.payload)} ctx={ctx} cb={cb} />
      );
    case 'read_aloud_passage':
      return (
        <ReadAloudPassage
          data={content.readAloudPassageData.parse(activity.payload)}
          ctx={ctx}
          cb={cb}
        />
      );
    case 'story_scene':
      return <StoryScene data={content.storySceneData.parse(activity.payload)} ctx={ctx} cb={cb} />;
    case 'printable_homework':
      return (
        <PrintableHomework
          data={content.printableHomeworkData.parse(activity.payload)}
          ctx={ctx}
          cb={cb}
        />
      );
  }
}

function Celebration({
  result,
  t,
  onExit,
}: {
  result: CompleteResult;
  t: TFn;
  onExit: () => void;
}): ReactElement {
  return (
    <div className="grid min-h-[70vh] place-items-center px-6">
      <div className="w-full max-w-sm text-center">
        <div className="text-7xl" aria-hidden="true">
          🎉
        </div>
        <h2 className="mt-4 text-3xl font-bold text-burgundy-700">
          {t('content.player.done_title')}
        </h2>
        <p className="mt-2 text-ink-700">{t('content.player.done_subtitle')}</p>

        <div className="mt-7 grid grid-cols-3 gap-3">
          <Stat label={t('content.player.stat_score')} value={`${result.score}%`} />
          <Stat label={t('content.player.stat_xp')} value={`+${result.xpAwarded}`} />
          <Stat
            label={t('content.player.stat_streak')}
            value={t('content.player.streak_days', { n: result.streak.current })}
          />
        </div>

        {/* Badges this lesson unlocked — the payoff moment (CLAUDE.md §25). */}
        {result.badgesEarned && result.badgesEarned.length > 0 ? (
          <div className="mt-6 rounded-3xl bg-gradient-to-b from-sun-50 to-cream-50 p-5 ring-2 ring-sun-300/60">
            <p className="text-sm font-bold uppercase tracking-wide text-sun-600">
              {t('app.badges.new_badge')}
            </p>
            <ul className="mt-3 flex flex-wrap justify-center gap-3">
              {result.badgesEarned.map((b) => (
                <li key={b.id} className="flex flex-col items-center">
                  <span className="text-4xl" aria-hidden="true">
                    🏅
                  </span>
                  <span className="mt-1 max-w-[7rem] text-xs font-bold leading-tight text-burgundy-700">
                    {t(b.titleKey)}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <button
          type="button"
          onClick={onExit}
          className="mt-8 inline-flex min-h-[56px] w-full items-center justify-center rounded-full bg-sun-500 px-10 text-lg font-bold text-burgundy-700 transition-colors hover:bg-sun-600 hover:text-cream-50"
        >
          {t('content.player.back_home')}
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }): ReactElement {
  return (
    <div className="rounded-2xl bg-white p-3 ring-1 ring-cream-200">
      <p className="text-xl font-bold text-burgundy-700">{value}</p>
      <p className="mt-0.5 text-xs text-ink-500">{label}</p>
    </div>
  );
}
